import { Link } from "react-router-dom";

export const TONE = {
  gold: "bg-primary/12 text-primary border-primary/25",
  green: "bg-emerald-500/12 text-emerald-400 border-emerald-500/25",
  red: "bg-red-500/12 text-red-400 border-red-500/25",
  amber: "bg-amber-500/12 text-amber-400 border-amber-500/25",
  blue: "bg-sky-500/12 text-sky-400 border-sky-500/25",
  violet: "bg-violet-500/12 text-violet-300 border-violet-500/25",
  neutral: "bg-white/5 text-muted-foreground border-white/10",
};

export const SectionLabel = ({ children, className = "" }) => (
  <div className={`label-mono ${className}`}>{children}</div>
);

/* Big serif title + muted scope line + right-side actions. */
export const PageHeader = ({ title, subtitle, actions, live, testid = "page-header" }) => (
  <div className="flex items-start justify-between gap-4 flex-wrap" data-testid={testid}>
    <div className="min-w-0">
      <div className="flex items-center gap-2.5">
        <h2 className="font-heading text-2xl sm:text-3xl truncate">{title}</h2>
        {live && <LivePill />}
      </div>
      {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

export const LivePill = ({ label = "Live" }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/12 border border-emerald-500/25 text-emerald-400 text-[10px] uppercase tracking-[0.18em]" data-testid="live-pill">
    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
    {label}
  </span>
);

/* Bordered surface with optional header strip. */
export const Panel = ({ label, title, icon: Icon, actions, children, testid, className = "", bodyClass = "", flush }) => (
  <div className={`border border-border bg-card ${className}`} data-testid={testid}>
    {(title || label || actions) && (
      <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          {label && <div className="label-mono">{label}</div>}
          {title && <h3 className="font-heading text-lg mt-0.5 truncate">{title}</h3>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          {Icon && <Icon size={17} className="text-primary" />}
        </div>
      </div>
    )}
    <div className={flush ? bodyClass : `p-5 ${bodyClass}`}>{children}</div>
  </div>
);

/* Horizontal segmented pill tabs — gold fill = active. */
export const PillTabs = ({ tabs, value, onChange, testid = "pill-tabs", size = "md" }) => {
  const pad = size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm";
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testid}>
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            data-testid={t.testid || `${testid}-${t.key}`}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 rounded-full border transition-colors duration-150 ${pad} ${
              active
                ? "bg-primary text-primary-foreground border-primary font-medium"
                : "bg-transparent border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
            }`}
          >
            {t.icon && <t.icon size={14} weight={active ? "fill" : "regular"} />}
            {t.label}
            {t.count > 0 && (
              <span className={`tnum text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-black/15" : "bg-white/8 text-muted-foreground"}`}>
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/* Tappable multi/single-select chip. */
export const Chip = ({ active, onClick, children, icon: Icon, testid, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    aria-pressed={!!active}
    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm transition-colors duration-150 disabled:opacity-40 ${
      active
        ? "bg-primary/15 border-primary text-primary"
        : "bg-transparent border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
    }`}
  >
    {Icon && <Icon size={15} />}
    {children}
  </button>
);

const STATUS_TONE = {
  available: "green", active: "green", confirmed: "green", completed: "neutral", paid: "green", approved: "green",
  current: "amber", in_progress: "amber", out: "amber", pre_booking: "amber", submitted: "amber", pending: "amber",
  invoiced: "blue", quoted: "blue", contacted: "blue", new: "blue", partial: "blue", negotiating: "blue",
  overdue: "red", rejected: "red", cancelled: "red", unpaid: "red", maintenance: "red", denied: "red",
};

export const StatusPill = ({ status, tone, toneClass, dot = true, plain = false, title, children, icon: Icon, testid, className = "" }) => {
  const key = String(status || "").toLowerCase();
  const t = tone || STATUS_TONE[key] || "neutral";
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${plain ? "" : "tracking-wide capitalize "}whitespace-nowrap ${toneClass || TONE[t]} ${className}`}
      data-testid={testid}
    >
      {Icon ? <Icon size={11} weight="fill" /> : dot ? <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" /> : null}
      {children || String(status || "").replace(/_/g, " ")}
    </span>
  );
};

/* Gold money figure. */
export const Money = ({ value, className = "", testid }) => (
  <span className={`money ${className}`} data-testid={testid}>
    {new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 2 }).format(Number(value) || 0)}
  </span>
);

export const EmptyState = ({ icon: Icon, title, hint, action, testid }) => (
  <div className="px-6 py-14 text-center" data-testid={testid}>
    {Icon && (
      <div className="w-11 h-11 mx-auto rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
        <Icon size={19} className="text-muted-foreground" />
      </div>
    )}
    <div className="font-heading text-lg">{title}</div>
    {hint && <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">{hint}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

/* Uppercase table header cell. */
export const Th = ({ children, align = "left", className = "" }) => (
  <th className={`label-mono font-normal px-4 py-3 text-${align} ${className}`}>{children}</th>
);

export const RowLink = ({ to, children, className = "", testid }) => (
  <Link to={to} className={`block hover:bg-white/[0.03] transition-colors duration-150 ${className}`} data-testid={testid}>
    {children}
  </Link>
);
